import { useState, useEffect } from "react";
import { IoTrash } from "react-icons/io5";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import Helpers from "@/Helpers";
import Modal from "@modalSrc/Modal";
import ModalFormFields from "@modalSrc/ModalFormFields";
import DateTimePicker from "@elements/DateTimePicker";

type InvoiceLineItem = {
  id: number;
  chargeDate: string;
  quantity: number;
  amount: number;
  companyFeeItemId: number | null;
  companyFeeItemName: string;
  testTypeName?: string;
  testUserName?: string;
  description?: string;
  testId?: number;
  entityId?: number;
  entityTypeId?: number;
  entityTypeName?: string;
  invoiceId?: number;
};

type FeeScheduleItem = {
  id: number;
  ItemName?: string;
  itemName?: string;
  name?: string;
  price?: number;
  Price?: number;
};

export default function InvoiceLineItemModal({ 
  onClose, 
  invoiceLineItemId, 
  companyId,
  invoiceId,
  adminPerms, 
  updateSpinner,
  onSave 
}) {
  // State management
  const [originalInvoiceLineItem, setOriginalInvoiceLineItem] = useState<InvoiceLineItem | null>(null);
  const [invoiceLineItem, setInvoiceLineItem] = useState<InvoiceLineItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [feeScheduleData, setFeeScheduleData] = useState<FeeScheduleItem[] | null>(null);

  // Initialize modal when opened
  useEffect(() => {
    if (invoiceLineItemId && invoiceLineItemId > 0) {
      fetchInvoiceLineItem();
    } else if (invoiceLineItemId === 0) {
      // Initialize for creating a new line item
      const newLineItem = {
        id: 0,
        chargeDate: new Date().toISOString().split('T')[0],
        quantity: 1,
        amount: 0,
        companyFeeItemId: null,
        companyFeeItemName: '',
        testTypeName: '',
        testUserName: '',
        description: ''
      };
      setInvoiceLineItem(newLineItem);
      setOriginalInvoiceLineItem(null);
      if(invoiceLineItemId === 0)
        getFeeScheduleAsync();
    } else {
      setInvoiceLineItem(null);
      setOriginalInvoiceLineItem(null);
    }
  }, [invoiceLineItemId]);



  async function fetchInvoiceLineItem() {
    setIsLoading(true);
    try {
      const response = await RightTimeApi.getAsync(`InvoiceLineItem/${invoiceLineItemId}`);
      if (response && response.ok && response.jsonData) {
        const lineItem = response.jsonData;
        // The API returns chargeDate as "2025-10-08T00:00:00", keep as-is for date input
        setOriginalInvoiceLineItem({ ...lineItem });
        setInvoiceLineItem({ ...lineItem });
      } else {
        toast.error("Failed to load invoice line item details");
      }
    } catch (error) {
      console.error("Error fetching invoice line item:", error);
      toast.error("Error loading invoice line item: " + error.message);
    } finally {
      setIsLoading(false);
    }
  }
  async function getFeeScheduleAsync() {
    if (!companyId) return;

    try {
      updateSpinner(true);
      const resp = await RightTimeApi.getAsync(`/company/${companyId}/FeeSchedule`);
      if (resp && resp.ok && resp.jsonData) {
        console.log("Fee Schedule Data:", resp.jsonData);
        setFeeScheduleData(resp.jsonData);
      } else
        toast.error("Failed to load fee schedule");
    } catch (error) {
      console.error("Error loading fee schedule:", error);
      toast.error("Error loading fee schedule");
    } finally {
      updateSpinner(false);
    }
  }

  function handleInvoiceLineItemPropertyChange(field, value, type = 'string') {
    let processedValue = value;
    
    if (type === 'int') {
      processedValue = value === '' ? null : parseInt(value);
    } else if (type === 'float') {
      processedValue = value === '' ? 0 : parseFloat(value);
    }
    
    setInvoiceLineItem(prev => ({ ...prev, [field]: processedValue }));
  }

  function hasDataChanged() {
    // For new line items, check if required fields are filled
    if (invoiceLineItem?.id === 0) {
      return hasRequiredFields();
    }
    
    // For existing line items, compare with original
    if (!originalInvoiceLineItem || !invoiceLineItem) return false;
    return JSON.stringify(originalInvoiceLineItem) !== JSON.stringify(invoiceLineItem);
  }

  function hasRequiredFields() {
    if (!invoiceLineItem) return false;
    
    // Check required fields for new line items
    return !!(
      invoiceLineItem.chargeDate &&
      invoiceLineItem.quantity > 0 &&
      invoiceLineItem.amount > 0 &&
      invoiceLineItem.companyFeeItemId
    );
  }

  function validateForm() {
    if (!invoiceLineItem) return false;
    
    if (!invoiceLineItem.chargeDate) {
      toast.error("Charge date is required");
      return false;
    }

    if (!invoiceLineItem.quantity || invoiceLineItem.quantity <= 0) {
      toast.error("Quantity must be greater than 0");
      return false;
    }

    // For new line items, amount is required and must be greater than 0
    if (invoiceLineItem.id === 0) {
      if (!invoiceLineItem.amount || invoiceLineItem.amount <= 0) {
        toast.error("Amount is required and must be greater than 0");
        return false;
      }
    } else {
      // For existing line items, amount just can't be negative
      if (invoiceLineItem.amount < 0) {
        toast.error("Amount cannot be negative");
        return false;
      }
    }

    if (!invoiceLineItem.companyFeeItemId) {
      toast.error("Company Fee Item is required");
      return false;
    }

    return true;
  }

  async function handleSave() {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      let response;
      if (invoiceLineItem.id > 0) {
        // Update existing line item
        response = await RightTimeApi.sendAsync(`Company/${companyId}/InvoiceLineItem/${invoiceLineItem.id}`, invoiceLineItem);
      } else {
        // Create new line item
        const lineItemData = { ...invoiceLineItem };
        if (invoiceId) 
          lineItemData.invoiceId = invoiceId;
        response = await RightTimeApi.postAsync(`Company/${companyId}/InvoiceLineItem`, lineItemData);
      }

      if (response && response.ok) {
        toast.success(invoiceLineItem.id > 0 ? "Invoice line item updated successfully" : "Invoice line item created successfully");
        onSave && onSave();
        onClose();
      } else {
        toast.error("Failed to save invoice line item");
      }
    } catch (error) {
      console.error("Error saving invoice line item:", error);
      toast.error("Error saving invoice line item: " + error.message);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm("Are you sure you want to delete this invoice line item?")) return;

    setIsSaving(true);
    try {
      const response = await RightTimeApi.sendAsync(`InvoiceLineItem/${invoiceLineItem.id}`, "DELETE");
      if (response && response.ok) {
        toast.success("Invoice line item deleted successfully");
        onSave && onSave();
        onClose();
      } else {
        toast.error("Failed to delete invoice line item");
      }
    } catch (error) {
      toast.error("Error deleting: " + error.message);
    } finally {
      setIsSaving(false);
    }
  }

  function getModalTitle() {
    if (isLoading) return "Loading...";
    if (invoiceLineItem && invoiceLineItem.id > 0) {
      return `Edit Invoice Line Item (${invoiceLineItem.id})`;
    }
    if (invoiceLineItemId === 0) {
      return "Create New Invoice Line Item";
    }
    return "Invoice Line Item";
  }

  function canEdit() {
    return true;
  }

  function canDelete() {
    return canEdit() && invoiceLineItem && invoiceLineItem.id > 0;
  }

  const invoiceLineItemFields = {
    'Charge Date': (
      <DateTimePicker
        field="chargeDate"
        pickerTitle="Charge Date"
        value={invoiceLineItem?.chargeDate ? invoiceLineItem.chargeDate.substring(0, 10) : ''}
        onChange={(e) => handleInvoiceLineItemPropertyChange('chargeDate', e?.target?.value || '')}
        disabled={!canEdit()}
        required
        dateOnly
      />
    ),
    'Quantity': (
      <input
        type="number"
        step="0.01"
        value={invoiceLineItem?.quantity || ''}
        onChange={(e) => handleInvoiceLineItemPropertyChange('quantity', e.target.value, 'float')}
        disabled={!canEdit()}
        required
      />
    ),
    'Amount': (
      <input
        type="number"
        step="0.01"
        value={invoiceLineItem?.amount || ''}
        onChange={(e) => handleInvoiceLineItemPropertyChange('amount', e.target.value, 'float')}
        disabled={!canEdit()}
        required
      />
    ),
    ...(invoiceLineItem?.id > 0 && invoiceLineItem?.testId ? {
      'Test Type Name': (
        <input
          type="text"
          value={invoiceLineItem?.testTypeName || ''}
          disabled={true}
          readOnly
        />
      ),
      'Test User Name': (
        <input
          type="text"
          value={invoiceLineItem?.testUserName || ''}
          disabled={true}
          readOnly
        />
      )
    } : {}),
    'Company Fee Item': invoiceLineItem?.id === 0 ? (
      <select
        value={invoiceLineItem?.companyFeeItemId || ''}
        onChange={(e) => {
          const selectedId = parseInt(e.target.value);
          const selectedItem = feeScheduleData?.find(item => item.id === selectedId);
          handleInvoiceLineItemPropertyChange('companyFeeItemId', selectedId, 'int');
          handleInvoiceLineItemPropertyChange('companyFeeItemName', selectedItem?.ItemName || '');
          // Only update amount if we have a valid price from the selected item
          if (selectedItem && (selectedItem.price || selectedItem.Price)) {
            handleInvoiceLineItemPropertyChange('amount', selectedItem.price || selectedItem.Price, 'float');
          }
        }}
        disabled={!canEdit()}
        required
      >
        <option value="">Select Fee Item</option>
        {feeScheduleData && feeScheduleData.length > 0 ? (
          feeScheduleData.map(item => {
            console.log("Fee Schedule Item:", item);
            return (
              <option key={item.id} value={item.id}>
                {item.ItemName || item.itemName || item.name || 'Unknown Item'} 
              </option>
            );
          })
        ) : (
          <option value="" disabled>No fee items available</option>
        )}
      </select>
    ) : (
      <input
        type="text"
        value={invoiceLineItem?.companyFeeItemName || ''}
        disabled={true}
        readOnly
      />
    ),
    ...(invoiceLineItem?.entityTypeId && invoiceLineItem?.entityTypeName && invoiceLineItem?.entityId ? {
      [invoiceLineItem.entityTypeName]: (
        <input
          type="number"
          value={invoiceLineItem.entityId || ''}
          onChange={(e) => handleInvoiceLineItemPropertyChange('entityId', e.target.value, 'int')}
          disabled={!canEdit()}
        />
      )
    } : {}),
    'Description': (
      <textarea
        value={invoiceLineItem?.description || ''}
        onChange={(e) => handleInvoiceLineItemPropertyChange('description', e.target.value)}
        disabled={!canEdit()}
        rows={3}
        placeholder="Optional description or notes"
      />
    )
  };

  const footer = (
    <>
      <div className="footerLeft">
        {canDelete() && (
          <button
            type="button"
            className="btn btn-danger iconButton"
            onClick={handleDelete}
            disabled={isSaving}
            title="Delete Line Item"
          >
            {isSaving ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <IoTrash />
            )}
          </button>
        )}
      </div>
      <div className="footerRight">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!canEdit() || !hasDataChanged() || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </>
  );

  if (invoiceLineItemId === null) return null;

  const children = isLoading ? (
    <div className="loading">Loading invoice line item details...</div>
  ) : invoiceLineItem ? (
    <ModalFormFields fields={invoiceLineItemFields} />
  ) : (
    <div className="error">Failed to load invoice line item data</div>
  );

  return (
    <Modal 
      isOpen={invoiceLineItemId !== null && invoiceLineItemId !== undefined} 
      onClose={onClose} 
      title={getModalTitle()}
      className="invoiceLineItemModal"
      updateSpinner={updateSpinner}
      footer={footer}
    >
      {children}
    </Modal>
  );
}

